import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toastr } from 'react-redux-toastr';

import { Button, withStyles, makeStyles } from '@material-ui/core';

import { getLocationItem } from '../../collums-constants/utils/index';

import Modal from '../common/Modal';
import Accordion from '../common/Accordion';
import { membershipsStyles } from './styles';
import { modalsButtonStyles } from '../../collums-constants/styles/stylesheets/buttonsStyles';

import GeneralTab from './tabs/GeneralTab';
import PriceAndLocationTab from './tabs/PriceAndLocationTab';
// import OnlinePurchasingTab from './tabs/OnlinePurchasingTab';
import CommissionTab from './tabs/CommissionTab';
import LoadingScreen from '../../collums-components/components/common/loadingScreen';

import ServiceApi from '../../api/ServiceApi';
import MembershipsApi from '../../api/MembershipsApi';
import ConfirmModal from '../common/ConfirmModal';

const validationMessages = {
    required: 'This field is required.',
    minValueZero: 'This value cannot be less than 0',
    minValueOne: 'This value cannot be less than 1'
};

function MembershipFormModal({
    open,
    title,
    onClose,
    classes,
    selectedMembership,
    getMembershipCategories,
    categories,
    setCategories,
    refreshList,
    errorMessage,
    getMembershipGrossPrice,
    getMembershipNetPrice,
    membershipTaxes,
    clinic,
    clinics,
    isFromOrg,
    allClinics
}) {
    const globalStyles = makeStyles(modalsButtonStyles)();
    const defaultValues = {
        name: selectedMembership.name || '',
        active: selectedMembership ? selectedMembership.active : true,
        code: selectedMembership.code || '',
        description: selectedMembership.description || '',
        services: selectedMembership.services || [],
        category: selectedMembership.category || '',
        subCategories: selectedMembership.category?.subCategories || [],
        tags: selectedMembership.tags || [],
        tax: isFromOrg ? selectedMembership.itemTax : getLocationItem(selectedMembership, clinic).itemTax,
        discount: isFromOrg
            ? selectedMembership.discount || 0
            : getLocationItem(selectedMembership, clinic).discount || 0,
        salePrice: selectedMembership.salePrice || 0,
        netPrice: selectedMembership.netPrice
            ? selectedMembership.netPrice
            : selectedMembership.salePrice
            ? selectedMembership.salePrice / (1 + (selectedMembership?.tax?.rate || 0) / 100)
            : 0,
        frequency: selectedMembership.frequency || '',
        allowOnlineBooking: selectedMembership ? selectedMembership.allowOnlineBooking : true,
        order: isFromOrg
            ? selectedMembership?.order || ''
            : getLocationItem(selectedMembership, clinic)?.order || selectedMembership?.order || '',
        onlineName: selectedMembership?.onlineName || '',
        onlineSubtitle: isFromOrg
            ? selectedMembership?.onlineSubtitle || ''
            : getLocationItem(selectedMembership, clinic)?.onlineSubtitle || selectedMembership?.onlineSubtitle || '',
        onlineDescription: selectedMembership?.onlineDescription || '',
        onlineInfoBubble: selectedMembership.onlineInfoBubble || '',
        bookingLink: selectedMembership.bookingLink || '',
        showOnline: selectedMembership ? selectedMembership.showOnline : true,
        displayDurationServices: selectedMembership.displayDurationServices || false,
        durationOfServices: selectedMembership.durationOfServices || 0,
        showPrice: selectedMembership.showPrice || false,
        onlinePrice: selectedMembership.onlinePrice || selectedMembership.salePrice,
        medical: selectedMembership.medical || '',
        expiryAfterMonths: selectedMembership.expiryAfterMonths || null
    };

    const { register, unregister, handleSubmit, control, watch, setValue, getValues, errors, reset } = useForm({
        defaultValues
    });
    const fields = undefined; // DO NOT use watch() without args - causes re-render on every field change

    // Function to calculate monthly price of a service
    const calculateMonthlyServicePrice = useCallback((service, quantity, serviceType, repeatEvery) => {
        const grossPrice = service?.defaultGrossPrice || 0;

        if (serviceType === 'one-off') {
            return grossPrice * quantity;
        }

        return (grossPrice * quantity) / repeatEvery;
    }, []);

    // Calculate suggested price from services
    const calculateSuggestedPrice = useCallback(
        services => {
            if (!services || services.length === 0) {
                return 0;
            }

            let totalGross = 0;
            services.forEach(svc => {
                if (svc.service) {
                    totalGross += calculateMonthlyServicePrice(
                        svc.service,
                        svc.quantity || 1,
                        svc.serviceType || 'one-off',
                        svc.repeatEvery || 1
                    );
                }
            });

            return Math.round(totalGross * 100) / 100;
        },
        [calculateMonthlyServicePrice]
    );

    // Watch services for changes
    const servicesWatch = watch('services');
    const suggestedPrice = calculateSuggestedPrice(servicesWatch);

    // Track if this is initial load (skip auto-calculation on first render for existing memberships)
    const isInitialLoad = useRef(true);
    const prevServicesJson = useRef(JSON.stringify(servicesWatch));

    const [services, setServices] = useState();
    const [filterServices, setFilterServices] = useState({ value: '' });
    const [isLoading, setIsLoading] = useState(false);

    const [confirmModal, setConfirmModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalContent, setModalContent] = useState('');
    const [modalCancel, setModalCancel] = useState(null);
    const [modalContinue, setModalContinue] = useState(null);
    const [modalOnContinue, setModalOnContinue] = useState(() => {});

    const onSubmit = async data => {
        if (!data.name?.trim()) {
            return toastr.error('Name is required');
        }
        if (!data.category) {
            return toastr.error('Category is required');
        }

        let oryginalData = { ...data };
        const newTags = data.tags.map(tag => tag.id);

        data.locations = data.locations?.filter(loc => loc.isSelected);
        if (!data.locations.length) {
            if (isFromOrg && allClinics.length === 1) {
                data.locations = allClinics.map(loc => {
                    return {
                        value: loc.id,
                        isSelected: true
                    };
                });
            } else {
                return toastr.error('Locations is a required field');
            }
        }

        if (data.locations?.length) {
            data.locations = data.locations
                .map(location => ({
                    clinic: location.value,
                    isSelected: location.isSelected
                }))
                .filter(loc => loc.isSelected);
        }

        if (!data?.tax) {
            return toastr.error('Tax is a required field');
        }

        if (!data?.salePrice) {
            return toastr.error('Price must be greater than 0');
        }

        if (!data?.services || data.services.length === 0) {
            return toastr.error('At least one service is required');
        }

        const hasRepeatingService = data.services.some(svc => svc.serviceType === 'repeating');
        if (!hasRepeatingService) {
            return toastr.error('Membership must include at least one repeating service');
        }

        // Validate services structure
        const servicesPayload = data.services.map(svc => {
            const serviceItem = {
                service: svc.service?.id || svc.service,
                quantity: svc.quantity || 1,
                serviceType: svc.serviceType || 'one-off',
                availableFrom: svc.availableFrom || 'immediately',
                rollover: svc.rollover || false
            };
            if (svc.serviceType === 'repeating') {
                serviceItem.repeatEvery = svc.repeatEvery || 1;
            }
            return serviceItem;
        });

        const body = {
            name: data.name,
            active: data.active || selectedMembership.active || false,
            archived: data.archived || selectedMembership.archived || false,
            code: data.code,
            description: data.description,
            services: servicesPayload,
            category: data.category.id,
            locations: data.locations || [],
            tags: newTags,
            tax: data.tax?.id,
            discount: parseFloat(data.discount) || 0,
            netPrice: parseFloat(data.netPrice) || 0,
            salePrice: parseFloat(data.salePrice) || 0,
            dateOfFirstInstallment: data.dateOfFirstInstallment,
            frequency: data.frequency,
            allowOnlineBooking: data.allowOnlineBooking,
            order: parseInt(data.order) || 0,
            onlineName: data.onlineName,
            onlineSubtitle: data.onlineSubtitle,
            onlineDescription: data.onlineDescription,
            onlineInfoBubble: data.onlineInfoBubble,
            bookingLink: data.bookingLink,
            showOnline: data.showOnline,
            displayDurationServices: data.displayDurationServices,
            durationOfServices: data.durationOfServices,
            showPrice: data.showPrice,
            onlinePrice: parseFloat(data.onlinePrice) || 0,
            allowCommission: data.allowCommission,
            medical: data.medical,
            expiryAfterMonths: data.expiryAfterMonths || null
        };

        if (selectedMembership.id) {
            try {
                if (data.name !== selectedMembership.prevName) {
                    setModalTitle('Confirm Editing');
                    setModalContent(
                        'Changing the name of this membership will also affect historic appointments, invoices, reports and anywhere else it features.\n' +
                            'If you wish to keep historic records the same but have this change for the future, please inactivate this membership and create a new one.'
                    );
                    setModalCancel('Cancel');
                    setModalContinue('Confirm');
                    setModalOnContinue(() => () => {
                        selectedMembership.prevName = data.name;
                        onSubmit(oryginalData);
                    });
                    setConfirmModal(true);
                    return;
                }
                if (!isFromOrg) {
                    body.salePrice = defaultValues.salePrice;
                    body.netPrice = defaultValues.netPrice;
                    body.discount = selectedMembership.discount;
                    body.tax = selectedMembership.tax;
                    body.allowCommission = selectedMembership.allowCommission;
                    body.category = selectedMembership.category?.id || selectedMembership.category;
                    body.locations = selectedMembership.locations.map(loc => {
                        if (loc.clinic === clinic) {
                            loc.salePrice = data.salePrice;
                            loc.netPrice = data.netPrice;
                            loc.discount = data.discount;
                            loc.tax = data.tax?.id;
                            loc.allowCommission = data.allowCommission;
                            loc.category = data.category.id;
                            return loc;
                        } else {
                            return loc;
                        }
                    });
                } else {
                    const oldLocationsClinics = selectedMembership?.locations?.map(loc => loc.clinic) || [];

                    body.locations = body.locations
                        .map(loc => {
                            if (oldLocationsClinics.includes(loc.clinic)) {
                                return selectedMembership.locations.find(_loc => _loc.clinic === loc.clinic);
                            } else {
                                loc.active = selectedMembership.active;
                                loc.archived = selectedMembership.archived;
                                loc.salePrice = data.salePrice;
                                loc.netPrice = data.netPrice;
                                loc.discount = data.discount;
                                loc.tax = data.tax?.id;
                                loc.allowCommission = data.allowCommission;
                                loc.category = data.category.id;
                                return loc;
                            }
                        })
                        .filter(el => el);
                }

                await MembershipsApi.updateMembership(selectedMembership.id, body, isFromOrg, clinic);
                toastr.success('Success', 'your records were successfully updated!');
            } catch (err) {
                if (typeof err === 'object') {
                    if (err.data && err.data.message) {
                        toastr.error(err.data.message);
                        return;
                    }
                }
                toastr.error(err?.data?.message || errorMessage.title, errorMessage.message);
            }
        } else {
            try {
                body.active = true;
                body.archived = false;
                body.locations = data.locations.map(loc => {
                    loc.salePrice = data.salePrice;
                    loc.netPrice = data.netPrice;
                    loc.discount = data.discount;
                    loc.tax = data.tax?.id;
                    loc.active = true;
                    loc.archived = false;
                    loc.allowCommission = data.allowCommission;
                    loc.category = data.category.id;
                    return loc;
                });
                await MembershipsApi.createMembership(body);
                toastr.success('Success', 'your records were successfully saved!');
            } catch (err) {
                if (typeof err === 'object') {
                    if (err.data && err.data.message) {
                        toastr.error(err.data.message);
                        return;
                    }
                }
                toastr.error(err?.data?.message || errorMessage.title, errorMessage.message);
            }
        }
        await refreshList();
        onClose();
    };

    const showErros = error => {
        for (let err in error) {
            if (error[err]) {
                let message = '';
                if (Array.isArray(error[err])) {
                    message = 'Please complete all required fields';
                } else {
                    message = error[err].message;
                }
                return toastr.error(`${err}`, `${message}`);
            }
        }
    };

    const getServices = async forcedFilterServices => {
        try {
            const result = await ServiceApi.getServices(
                forcedFilterServices ? forcedFilterServices : filterServices,
                isFromOrg,
                clinic,
                999
            );

            const resultSorted = result.items.sort((a, b) => {
                const aToLower = a?.name?.toLowerCase();
                const bToLower = b?.name?.toLowerCase();

                if (aToLower < bToLower) {
                    return -1;
                }
                if (aToLower > bToLower) {
                    return 1;
                }
                return 0;
            });
            setServices(resultSorted);
        } catch {
            toastr.error(errorMessage.title, errorMessage.message);
        }
    };

    const loadData = async () => {
        try {
            setIsLoading(true);
            await getServices();
            await getMembershipCategories({ name: '' });
        } finally {
            setIsLoading(false);
        }
    };

    const formProps = {
        classes,
        validationMessages,
        register,
        unregister,
        control,
        fields,
        setValue,
        watch,
        isFromOrg,
        clinic,
        services,
        clinics,
        setServices,
        getServices,
        categories,
        setCategories,
        getMembershipCategories,
        filterServices,
        setFilterServices,
        getValues,
        getMembershipNetPrice,
        getMembershipGrossPrice,
        selectedMembership,
        membershipTaxes,
        allClinics,
        errors,
        suggestedPrice
    };

    useEffect(() => {
        loadData();
        /* eslint-disable-next-line */
    }, []);

    // Update prices when services change (skip initial load for existing memberships)
    useEffect(() => {
        const currentJson = JSON.stringify(servicesWatch);

        // Skip if services haven't actually changed
        if (currentJson === prevServicesJson.current) {
            return;
        }
        prevServicesJson.current = currentJson;

        // Skip first change for existing memberships (keeps saved price on initial load)
        if (isInitialLoad.current && selectedMembership.id) {
            isInitialLoad.current = false;
            return;
        }
        isInitialLoad.current = false;

        // Update prices based on services
        if (!servicesWatch || servicesWatch.length === 0) {
            setValue('salePrice', 0);
            setValue('netPrice', 0);
            return;
        }

        const discount = watch('discount') || 0;
        const totalGross = suggestedPrice * (1 - discount / 100);
        const taxRate = watch('tax')?.rate || 0;
        const totalNet = totalGross / (1 + taxRate / 100);

        setValue('salePrice', Math.round(totalGross * 100) / 100);
        setValue('netPrice', Math.round(totalNet * 100) / 100);
        /* eslint-disable-next-line */
    }, [JSON.stringify(servicesWatch)]);

    useEffect(() => {
        showErros(errors);
        // eslint-disable-next-line
    }, [Object.keys(errors).join(',')]);

    useEffect(() => {
        if (selectedMembership.id) {
            const newDefaultValues = {
                name: selectedMembership.name || '',
                active: selectedMembership ? selectedMembership.active : true,
                code: selectedMembership.code || '',
                description: selectedMembership.description || '',
                services: selectedMembership.services || [],
                category: selectedMembership.category || '',
                subCategories: selectedMembership.category?.subCategories || [],
                tags: selectedMembership.tags || [],
                tax: isFromOrg ? selectedMembership.itemTax : getLocationItem(selectedMembership, clinic).itemTax,
                discount: isFromOrg
                    ? selectedMembership.discount || 0
                    : getLocationItem(selectedMembership, clinic).discount || 0,
                salePrice: selectedMembership.salePrice || 0,
                netPrice: selectedMembership.netPrice
                    ? selectedMembership.netPrice
                    : selectedMembership.salePrice
                    ? selectedMembership.salePrice / (1 + (selectedMembership?.tax?.rate || 0) / 100)
                    : 0,
                frequency: selectedMembership.frequency || '',
                allowOnlineBooking: selectedMembership ? selectedMembership.allowOnlineBooking : true,
                order: isFromOrg
                    ? selectedMembership?.order || ''
                    : getLocationItem(selectedMembership, clinic)?.order || selectedMembership?.order || '',
                onlineName: selectedMembership?.onlineName || '',
                onlineSubtitle: isFromOrg
                    ? selectedMembership?.onlineSubtitle || ''
                    : getLocationItem(selectedMembership, clinic)?.onlineSubtitle ||
                      selectedMembership?.onlineSubtitle ||
                      '',
                onlineDescription: selectedMembership?.onlineDescription || '',
                onlineInfoBubble: selectedMembership.onlineInfoBubble || '',
                bookingLink: selectedMembership.bookingLink || '',
                showOnline: selectedMembership ? selectedMembership.showOnline : true,
                displayDurationServices: selectedMembership.displayDurationServices || false,
                durationOfServices: selectedMembership.durationOfServices || 0,
                showPrice: selectedMembership.showPrice || false,
                onlinePrice: selectedMembership.onlinePrice || selectedMembership.salePrice,
                medical: selectedMembership.medical || ''
            };
            reset(newDefaultValues);
        }
        // eslint-disable-next-line
    }, [selectedMembership.id]);

    const closeConfirmModal = () => {
        setConfirmModal(false);
        setModalTitle('');
        setModalContent();
        setModalContinue(null);
        setModalCancel(null);
    };

    if (isLoading) return <LoadingScreen />;
    return (
        <>
            <Modal
                id="MembershipFormModal"
                isOpen={open}
                title={title}
                size={'md'}
                onClose={onClose}
                onCancel={onClose}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={classes.accordions}>
                        <Accordion title={'General'}>
                            <GeneralTab {...formProps} key={'general'} />
                        </Accordion>
                        <Accordion title={isFromOrg && allClinics.length > 1 ? 'Price & Locations' : 'Price'}>
                            <PriceAndLocationTab {...formProps} />
                        </Accordion>
                        {/* <Accordion title={'Online Purchasing'}>
                            <OnlinePurchasingTab {...formProps} currentMembership={selectedMembership} />
                        </Accordion> */}
                        <Accordion title={'Commission'}>
                            <CommissionTab {...formProps} />
                        </Accordion>
                    </div>

                    <div className={globalStyles.buttonsContainer}>
                        <Button className={globalStyles.cancelButton} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button className={globalStyles.confirmButton} type="submit" variant="outlined">
                            Save
                        </Button>
                    </div>
                </form>
            </Modal>
            {confirmModal && (
                <ConfirmModal
                    isOpen
                    setIsOpen={closeConfirmModal}
                    onConfirm={modalOnContinue}
                    title={modalTitle}
                    content={modalContent}
                    centerContent
                    continueText={modalContinue}
                    cancelText={modalCancel}
                />
            )}
        </>
    );
}

export default withStyles(membershipsStyles)(MembershipFormModal);
